import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { DataTable } from '@/components/DataTable'
import { InfoTip } from '@/components/InfoTip'
import { LocalDate } from '@/components/LocalDate'
import { PageHeader } from '@/components/PageHeader'
import { PendingButton } from '@/components/PendingButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  type AdminCouponRow,
  adminDeleteCoupon,
  adminListCouponRedemptions,
  adminListCoupons,
  adminSaveCoupon,
} from '@/lib/billing/admin/server'
import { formatMicroUsdWithCurrency } from '@/lib/billing/billing'
import {
  COUPON_DURATIONS,
  type CouponDuration,
  type CouponKind,
  type CouponState,
  couponState,
  describeCouponAmount,
} from '@/lib/billing/coupons'
import { getPublicPlans } from '@/lib/billing/server'

export const Route = createFileRoute('/_authenticated/admin/coupons')({
  component: AdminCouponsPage,
})

const QUERY_KEY = ['admin-coupons'] as const

const DURATION_LABELS: Record<CouponDuration, string> = {
  once: 'First month only',
  repeating: 'A set number of months',
  forever: 'Every month',
}

const STATE_LABELS: Record<CouponState, string> = {
  live: 'Live',
  scheduled: 'Scheduled',
  expired: 'Expired',
  exhausted: 'Used up',
  off: 'Off',
}

/** ISO instant → value for a datetime-local input, in the admin's local time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** datetime-local value (admin's local time) → ISO instant for the server. */
function toInstant(local: string): string | null {
  return local ? new Date(local).toISOString() : null
}

/** The row's own rules, as the shared `couponState` wants them. */
function stateOf(row: AdminCouponRow): CouponState {
  return couponState(
    {
      active: row.active,
      startsAt: row.startsAt ? new Date(row.startsAt) : null,
      endsAt: row.endsAt ? new Date(row.endsAt) : null,
      maxRedemptions: row.maxRedemptions,
    },
    row.redemptions,
    new Date(),
  )
}

function StateBadge({ row }: { row: AdminCouponRow }) {
  const state = stateOf(row)
  if (state === 'live') return <Badge>Live</Badge>
  if (state === 'scheduled') return <Badge variant="outline">Scheduled</Badge>
  return <Badge variant="secondary">{STATE_LABELS[state]}</Badge>
}

/** What the code covers, as one short phrase rather than three columns. */
function appliesLabel(row: AdminCouponRow): string {
  const parts = [
    row.appliesToSubscriptions && 'Card plans',
    row.appliesToTerms && 'Crypto terms',
    row.appliesToTopUps && 'Added data',
  ].filter(Boolean)
  const where = parts.join(', ')
  return row.planSlugs.length > 0
    ? `${where} · ${row.planSlugs.join(', ')}`
    : where
}

/** The redemption log for one code, loaded only when its dialog is open. */
function RedemptionList({ couponId }: { couponId: string }) {
  const query = useQuery({
    queryKey: ['admin-coupon-redemptions', couponId],
    queryFn: () => adminListCouponRedemptions({ data: { couponId } }),
  })
  if (query.isPending) {
    return <p className="text-xs text-muted-foreground">Loading…</p>
  }
  if (!query.data?.length) {
    return (
      <p className="text-xs text-muted-foreground">
        Not redeemed by anyone yet.
      </p>
    )
  }
  return (
    <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
      {query.data.map((r) => (
        <li key={r.id} className="flex items-baseline justify-between gap-2">
          <span className="truncate">
            {r.organizationName ?? r.organizationId}
          </span>
          <span className="tabular-nums">
            {r.discountMicroUsd > 0
              ? formatMicroUsdWithCurrency(r.discountMicroUsd)
              : '—'}{' '}
            · <LocalDate iso={r.createdAt} />
          </span>
        </li>
      ))}
    </ul>
  )
}

function CouponDialog({
  row,
  onClose,
}: {
  /** null = creating. */
  row: AdminCouponRow | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const plansQuery = useQuery({
    queryKey: ['public-plans'],
    queryFn: () => getPublicPlans(),
  })

  const [code, setCode] = useState(row?.code ?? '')
  const [kind, setKind] = useState<CouponKind>(row?.kind ?? 'percent')
  const [percentOff, setPercentOff] = useState(String(row?.percentOff ?? 20))
  const [amountOffUsd, setAmountOffUsd] = useState(
    row?.amountOffMicroUsd ? String(row.amountOffMicroUsd / 1_000_000) : '10',
  )
  const [duration, setDuration] = useState<CouponDuration>(
    row?.duration ?? 'once',
  )
  const [durationInMonths, setDurationInMonths] = useState(
    String(row?.durationInMonths ?? 3),
  )
  const [subs, setSubs] = useState(row?.appliesToSubscriptions ?? true)
  const [terms, setTerms] = useState(row?.appliesToTerms ?? true)
  const [topUps, setTopUps] = useState(row?.appliesToTopUps ?? false)
  const [planSlugs, setPlanSlugs] = useState<string[]>(row?.planSlugs ?? [])
  const [maxRedemptions, setMaxRedemptions] = useState(
    row?.maxRedemptions != null ? String(row.maxRedemptions) : '',
  )
  const [oncePerOrg, setOncePerOrg] = useState(row?.oncePerOrg ?? true)
  const [startsAt, setStartsAt] = useState(toLocalInput(row?.startsAt ?? null))
  const [endsAt, setEndsAt] = useState(toLocalInput(row?.endsAt ?? null))
  const [active, setActive] = useState(row?.active ?? true)
  const [note, setNote] = useState(row?.note ?? '')

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: QUERY_KEY })

  const save = useMutation({
    mutationFn: () =>
      adminSaveCoupon({
        data: {
          ...(row ? { id: row.id } : {}),
          code,
          kind,
          percentOff: kind === 'percent' ? Number(percentOff) : null,
          amountOffUsd: kind === 'fixed' ? Number(amountOffUsd) : null,
          duration,
          durationInMonths:
            duration === 'repeating' ? Number(durationInMonths) : null,
          appliesToSubscriptions: subs,
          appliesToTerms: terms,
          appliesToTopUps: topUps,
          planSlugs,
          maxRedemptions: maxRedemptions ? Number(maxRedemptions) : null,
          oncePerOrg,
          startsAt: toInstant(startsAt),
          endsAt: toInstant(endsAt),
          active,
          note,
        },
      }),
    onSuccess: () => {
      void invalidate()
      toast.success('Saved')
      onClose()
    },
    onError: (e) =>
      toast.error("Couldn't save", { description: (e as Error).message }),
  })

  const remove = useMutation({
    mutationFn: () => adminDeleteCoupon({ data: { id: row?.id ?? '' } }),
    onSuccess: () => {
      void invalidate()
      toast.success('Deleted')
      onClose()
    },
    onError: (e) =>
      toast.error("Couldn't delete", { description: (e as Error).message }),
  })

  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{row ? `Edit ${row.code}` : 'New coupon'}</DialogTitle>
        <DialogDescription>
          Customers type the code at checkout. It is validated here, on every
          rail — the discount is only mirrored to Stripe so that card renewals
          stay discounted.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="coupon-code">Code</Label>
            <Input
              id="coupon-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="LAUNCH20"
              className="font-mono"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="coupon-kind">Discount</Label>
            <div className="flex gap-2">
              <Select
                value={kind}
                onValueChange={(v) => setKind(v as CouponKind)}
              >
                <SelectTrigger id="coupon-kind" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent</SelectItem>
                  <SelectItem value="fixed">Fixed amount</SelectItem>
                </SelectContent>
              </Select>
              {kind === 'percent' ? (
                <Input
                  aria-label="Percent off"
                  type="number"
                  min={1}
                  max={99}
                  value={percentOff}
                  onChange={(e) => setPercentOff(e.target.value)}
                  className="w-24"
                />
              ) : (
                <Input
                  aria-label="Dollars off"
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={amountOffUsd}
                  onChange={(e) => setAmountOffUsd(e.target.value)}
                  className="w-24"
                />
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label
              htmlFor="coupon-duration"
              className="flex items-center gap-1.5"
            >
              Lasts
              {duration !== 'once' ? (
                <InfoTip label="About switching a recurring code off">
                  Switching this code off later stops new customers redeeming
                  it, but does not end discounts already running. A recurring
                  discount lives on the customer's Stripe subscription and keeps
                  applying{' '}
                  {duration === 'forever'
                    ? 'to every renewal until that subscription ends'
                    : `for its ${durationInMonths || 'N'} months`}
                  . Ending one early is a manual action in the Stripe dashboard.
                </InfoTip>
              ) : null}
            </Label>
            <Select
              value={duration}
              onValueChange={(v) => setDuration(v as CouponDuration)}
            >
              <SelectTrigger id="coupon-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUPON_DURATIONS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {DURATION_LABELS[d]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Counted in billed months on both plan rails: a prepaid term is
              these months collected at once, so this decides how many of them
              are discounted. Added data is not billed by time, so a code always
              applies once there.
            </p>
          </div>
          {duration === 'repeating' ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor="coupon-months">Months</Label>
              <Input
                id="coupon-months"
                type="number"
                min={1}
                max={36}
                value={durationInMonths}
                onChange={(e) => setDurationInMonths(e.target.value)}
              />
            </div>
          ) : null}
        </div>

        <Separator />

        <div className="flex flex-col gap-2">
          <Label>Applies to</Label>
          <div className="flex flex-col gap-2 rounded-md border p-3">
            <ToggleRow
              id="coupon-subs"
              label="Card plans"
              hint="Monthly subscriptions. The discount is mirrored to Stripe."
              checked={subs}
              onChange={setSubs}
            />
            <ToggleRow
              id="coupon-terms"
              label="Crypto terms"
              hint="Prepaid fixed-length terms, and admin invoices for one."
              checked={terms}
              onChange={setTerms}
            />
            <ToggleRow
              id="coupon-topups"
              label="Added data"
              hint="Top-ups. The customer gets the full GB at the lower price."
              checked={topUps}
              onChange={setTopUps}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label>Plans</Label>
          <ToggleGroup
            type="multiple"
            variant="outline"
            spacing={2}
            value={planSlugs}
            onValueChange={setPlanSlugs}
            className="flex-wrap justify-start"
          >
            {(plansQuery.data?.plans ?? []).map((p) => (
              <ToggleGroupItem key={p.slug} value={p.slug}>
                {p.name}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <p className="text-xs text-muted-foreground">
            {planSlugs.length === 0
              ? 'Every plan.'
              : 'Only the selected plans.'}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="coupon-max">Total redemptions</Label>
            <Input
              id="coupon-max"
              type="number"
              min={1}
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              placeholder="Unlimited"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="coupon-note">Note</Label>
            <Input
              id="coupon-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Q3 launch campaign"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="coupon-starts">Starts (optional)</Label>
            <Input
              id="coupon-starts"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="coupon-ends">Ends (optional)</Label>
            <Input
              id="coupon-ends"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 rounded-md border p-3">
          <ToggleRow
            id="coupon-once"
            label="Once per organization"
            hint="An organization can redeem this code a single time."
            checked={oncePerOrg}
            onChange={setOncePerOrg}
          />
          <ToggleRow
            id="coupon-active"
            label="Active"
            hint="The hand switch; the start/end window applies on top."
            checked={active}
            onChange={setActive}
          />
        </div>

        {row ? (
          <div className="flex flex-col gap-2">
            <Label>Redeemed by</Label>
            <RedemptionList couponId={row.id} />
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <PendingButton
            pending={save.isPending}
            onClick={() => save.mutate()}
            disabled={!code}
          >
            Save
          </PendingButton>
          {row ? (
            <PendingButton
              variant="ghost"
              className="ml-auto text-destructive"
              pending={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Delete
            </PendingButton>
          ) : null}
        </div>
      </div>
    </DialogContent>
  )
}

function ToggleRow({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string
  label: string
  hint: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <Label htmlFor={id}>{label}</Label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  )
}

function AdminCouponsPage() {
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => adminListCoupons(),
  })
  // 'new' | row-id | null — which dialog is open.
  const [editing, setEditing] = useState<string | null>(null)
  // Codes the trial nudge mints for one customer each. Off by default so the
  // full list is the truth; on to see only the hand-made codes.
  const [hideGenerated, setHideGenerated] = useState(false)

  const columns: ColumnDef<AdminCouponRow>[] = [
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => (
        <button
          type="button"
          className="text-left font-mono font-medium hover:underline"
          onClick={() => setEditing(row.original.id)}
        >
          {row.original.code}
        </button>
      ),
    },
    {
      id: 'origin',
      header: '',
      cell: ({ row }) =>
        row.original.organizationId ? (
          <Badge
            variant="outline"
            title={
              row.original.organizationName
                ? `Auto-generated for ${row.original.organizationName}`
                : 'Auto-generated'
            }
          >
            Auto
          </Badge>
        ) : null,
    },
    {
      id: 'discount',
      header: 'Discount',
      cell: ({ row }) => (
        <span className="tabular-nums">
          {describeCouponAmount(row.original)}
        </span>
      ),
    },
    {
      id: 'applies',
      header: 'Applies to',
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {appliesLabel(row.original)}
        </span>
      ),
    },
    {
      id: 'redemptions',
      header: 'Redeemed',
      cell: ({ row }) => (
        <span className="tabular-nums">
          {row.original.redemptions}
          {row.original.maxRedemptions != null
            ? ` / ${row.original.maxRedemptions}`
            : ''}
        </span>
      ),
    },
    {
      id: 'state',
      header: 'State',
      cell: ({ row }) => <StateBadge row={row.original} />,
    },
  ]

  const editingRow =
    editing && editing !== 'new'
      ? (query.data?.find((r) => r.id === editing) ?? null)
      : null

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Coupons"
        description="Discount codes customers can enter at checkout, on cards, crypto terms and added data."
        actions={
          <div className="flex items-center gap-4">
            <label
              htmlFor="hide-generated-coupons"
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <Switch
                id="hide-generated-coupons"
                checked={hideGenerated}
                onCheckedChange={setHideGenerated}
              />
              Hide auto-generated
            </label>
            <Button onClick={() => setEditing('new')}>
              <Plus data-icon="inline-start" />
              New
            </Button>
          </div>
        }
      />
      <DataTable
        columns={columns}
        data={
          hideGenerated
            ? query.data?.filter((r) => !r.organizationId)
            : query.data
        }
        isPending={query.isPending}
        emptyMessage="No coupons yet."
      />
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
      >
        {editing !== null ? (
          <CouponDialog
            key={editing}
            row={editingRow}
            onClose={() => setEditing(null)}
          />
        ) : null}
      </Dialog>
    </div>
  )
}
